import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonFunctions = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Python Functions: The Building Blocks</h1>
                <p className="subtitle">Defining logic, isolating scope, and building reusable modular code.</p>
            </div>

            <div className="content-section">
                <h2>The Anatomy of a Function</h2>
                <p>
                    A function is a named block of code that performs a specific task. In Python, logical units are defined using the <code>def</code> keyword.
                    But before we look at the syntax, we must understand the <strong>Philosophy of Functions</strong>.
                </p>
                <p>
                    Programming is partially about managing complexity. As your scripts grow from 10 lines to 10,000 lines, they become impossible to read if everything is just one long list of instructions.
                    Functions allow you to apply the <strong>DRY Principle (Don't Repeat Yourself)</strong>.
                </p>
                <p>
                    Instead of copy-pasting the same tax-calculation logic in 50 different places (and risking bugs if you need to change the tax rate), you define it <em>once</em> in a function.
                    If the law changes, you update one block of code, and the entire system updates automatically. This is called <strong>Abstraction</strong>hiding the complex details behind a simple command name.
                </p>
                <p>
                    Python functions are unique compared to languages like Java or C++.
                </p>
                <p>
                    In Python, functions are <strong>First-Class Objects</strong>. This means they are treated exactly like any other variable (integers, strings, lists).
                    You can:
                </p>
                <ul>
                    <li>Assign them to variables.</li>
                    <li>Store them in lists or dictionaries.</li>
                    <li>Pass them as arguments to other functions (Callbacks).</li>
                    <li>Return them from other functions (Closures).</li>
                </ul>

                <CodeBlock language="python" code={`# 1. Defining a function
def calculate_tax(amount, rate=0.08):
    """Calculates sales tax regarding the rate."""
    return amount * rate

# 2. Assigning function to a variable
my_math_logic = calculate_tax

# 3. Calling the variable!
print(my_math_logic(100)) # Output: 8.0`} />
            </div>

            <div className="content-section">
                <h2>The Beginner's Trap: `return` vs `print`</h2>
                <p>
                    One of the most common mistakes new developers make is confusing `print()` (displaying output to a human) with `return` (sending data back to the program).
                </p>
                <p>
                    Think of a function like a specialized chef in a restaurant kitchen.
                </p>
                <ul>
                    <li><strong>`return`:</strong> The chef cooks the steak and puts it on the pass. The waiter (the main program) can then take that steak and give it to a customer, or cut it up, or put sauce on it. The result is <em>tangible data</em> delivered back to you.</li>
                    <li><strong>`print`:</strong> The chef just screams "I COOKED A STEAK!" into the void. No food appears on the pass. The waiter has nothing to carry. The program cannot <em>use</em> the result because it was merely displayed, not handed over.</li>
                </ul>
                <p>
                    If you try to do math with a `print` statement, Python will complain that you are trying to add numbers to `None`, because a function without a return implies `return None`.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Why `return` Matters</span>
                    </div>
                    <CodeBlock language="python" code={`def add_print(a, b):
    print(a + b) #  Just displays text

def add_return(a, b):
    return a + b # Returns usable data

# Scenario: We want to calculate (3 + 4) * 2

# Using print:
result = add_print(3, 4) # Prints "7" to console
# variable 'result' is actually None!
# total = result * 2  #  TypeError: unsupported operand type(s) for *: 'NoneType' and 'int'

# Using return:
result = add_return(3, 4) # result is integer 7
total = result * 2        # Works perfectly (14)
print(total)`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Modern Standards: Type Hints & Docstrings</h2>
                <p>
                    Writing "code that works" is easy. Writing code that <em>scales</em> requires documentation.
                    Modern Python (3.5+) relies heavily on <strong>Type Hinting</strong>.
                </p>
                <p>
                    Imagine coming back to your code 6 months later. You see a function `process(data)`. What is `data`? Is it a list? A dictionary? A custom object? Without digging into the code, you have no idea.
                </p>
                <p>
                    Type hints solve this by acting as "live documentation" that your IDE (like VS Code or PyCharm) can read. This powers the <strong>Autocomplete</strong> characteristic that makes modern coding so fast.
                </p>
                <ProTip><p>
                        <strong>Note:</strong> Python is still <em>dynamically typed</em>. Type hints are ignored by the interpreter at runtime.
                        They exist solely for developers and static analysis tools (like mypy).
                    </p></ProTip>

                <CodeBlock language="python" code={`#  Old School (Ambiguous)
def create_user(name, age, active):
    # Is age a string or int? Is active a boolean? 
    # Who knows? You have to read the code to find out.
    pass

# Modern Standard (Self-Documenting)
def create_user(name: str, age: int, active: bool = False) -&gt; dict:
    """
    Creates a new user profile.
    
    Args:
        name (str): Full display name.
        age (int): User age in years.
        active (bool): Account status flag.
        
    Returns:
        dict: A dictionary containing user metadata.
    """
    return {
        "id": 1,
        "name": name,
        "is_active": active
    }

# Your IDE now knows exactly what to suggest!`} />
            </div>

            <div className="content-section">
                <h2>Under the Hood: The Call Stack</h2>
                <p>
                    When you call a function, Python doesn't just jump to that code. It creates a <strong>Stack Frame</strong> in memory.
                    This frame is a temporary workspace that stores:
                </p>
                <ul>
                    <li><strong>Local Variables:</strong> Variables defined inside the function.</li>
                    <li><strong>Arguments:</strong> The values passed to the function parameters.</li>
                    <li><strong>Return Address:</strong> The specific line number to go back to when the function finishes.</li>
                </ul>
                <p>
                    These frames are stacked on top of each other, just like a stack of dinner plates.
                    The "Active" function is always the plate on the very top.
                </p>
                <p>
                    When Function A calls Function B:
                </p>
                <ol>
                    <li>Python pauses Function A (saves its instruction pointer).</li>
                    <li>Python pushes a new frame for Function B onto the stack.</li>
                    <li>Function B runs until completion.</li>
                    <li>Function B's frame is "Popped" (destroyed/cleaned up from memory).</li>
                    <li>Python looks at the saved pointer in Function A and resumes exactly where it left off.</li>
                </ol>
                <ProTip><p>
                        <strong>The Stack Overflow:</strong> Computer memory (RAM) is finite. The "Stack" has a strict size limit (usually related to recursion depth, default 1000 in Python).
                        If you have a recursive function without a base case, it keeps adding frames until it hits the ceiling, causing the interpreter to crash with a `RecursionError`.
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>Performance Pattern: Memoization</h2>
                <p>
                    <strong>Memoization</strong> is a fancy word for "caching the result of a function call".
                    It is a classic example of the <strong>Space-Time Tradeoff</strong> in computer science: we sacrifice some Memory (Space) to store previous results, in order to gain massive Speed (Time).
                    If a function is <em>pure</em> and computationally expensive, you should never calculate the same input twice.
                </p>

                <CodeBlock language="python" code={`import time
from functools import lru_cache

# Without Cache (Slow!)
def slow_fib(n):
    if n < 2: return n
    return slow_fib(n-1) + slow_fib(n-2)

# With Cache (Instant!)
# lru_cache = Least Recently Used Cache
@lru_cache(maxsize=None)
def fast_fib(n):
    if n < 2: return n
    return fast_fib(n-1) + fast_fib(n-2)

start = time.time()
print(fast_fib(100)) # Calculates instantly
print(f"Time: {time.time() - start:.5f}s")`} />
            </div>

            <div className="content-section">
                <h2>Advanced: Functions as First-Class Citizens</h2>
                <p>
                    In Python, functions are objects. This sounds like trivia, but it enables powerful patterns like <strong>Higher-Order Functions</strong> (functions that accept or return other functions).
                </p>

                <h3>1. Callbacks (Passing logic as data)</h3>
                <p>
                    Instead of hardcoding logic, you can pass a function <em>into</em> another function to customize behavior.
                    Think of this like giving someone a **Universal Remote Control**. You don't know *what* button they will press, but you give them the ability to execute an action at the right time.
                </p>
                <CodeBlock language="python" code={`def apply_operation(x, y, operation):
    """
    Takes two numbers AND a function logic.
    """
    return operation(x, y)

def add(a, b): return a + b
def multiply(a, b): return a * b

print(apply_operation(5, 3, add))      # 8
print(apply_operation(5, 3, multiply)) # 15

# This is how map(), filter(), and sorted() work!`} />
            </div>

            <div className="content-section">
                <h2>Recursion: Functions Calling Themselves</h2>
                <p>
                    Recursion is a technique where a function calls itself to solve smaller instances of the same problem.
                    It is heavily used in traversing tree structures (like file systems or HTML DOMs).
                </p>

                <ProTip><p><strong>Warning:</strong> Python has a recursion limit (usually 1000 stack frames).
                        Unlike functional languages (Haskell, Lisp), Python does <em>not</em> optimize Tail-Recursion.
                        For very deep iteration, prefer a `while` loop (or increase `sys.setrecursionlimit`).</p></ProTip>

                <CodeBlock language="python" code={`# Calculating Factorial (5! = 5 * 4 * 3 * 2 * 1)

def factorial(n):
    # 1. Base Case: When to stop
    if n == 1:
        return 1
    
    # 2. Recursive Case: Call self with smaller input
    return n * factorial(n - 1)

print(factorial(5)) # 120`} />
            </div>

            <div className="content-section">
                <h2>Advanced: Closures and Factories</h2>
                <p>
                    Since functions are objects, you can define a function <strong>inside</strong> another function.
                    If the inner function uses variables from the outer function, it forms a <strong>Closure</strong>.
                </p>
                <p>
                    <strong>The Backpack Analogy:</strong> When the inner function is returned, it packs a "backpack" containing all the variables it needs from the outer scope.
                    Even though the outer function finishes and its memory frame is destroyed, the inner function keeps its backpack safe. This allows it to "remember" the environment in which it was created.
                </p>
                <p>
                    This is incredibly powerful for creating "Function Factories"functions that build other functions.
                </p>

                <CodeBlock language="python" code={`def power_factory(exponent):
    """
    Returns a NEW function that raises numbers to the given exponent.
    """
    def inner_power_logic(base):
        # 'exponent' is remembered from the outer scope!
        return base ** exponent
    
    return inner_power_logic

# Create specialized functions
square = power_factory(2)
cube = power_factory(3)

print(square(5)) # 25
print(cube(5))   # 125

# Why use this? 
# It avoids global variables and keeps configuration (exponent) hidden.`} />
            </div>

            <div className="content-section">
                <h2>Introduction to Decorators</h2>
                <p>
                    If you understand Closures and Callbacks, you understand <strong>Decorators</strong>.
                    A decorator is just a function that takes another function, wraps it in some extra code, and returns the wrapper.
                </p>
                <p>
                    <strong>The Gift Wrap Analogy:</strong> Think of your function as a gift. A decorator is the wrapping paper.
                    It adds something to the presentation (logging, timing, authentication) without changing the gift inside.
                    When you call the decorated function, you are "unwrapping" it to get to the real logic.
                </p>
                <p>
                    They are processed using the <code>@symbol</code> syntax.
                </p>

                <CodeBlock language="python" code={`def logger_decorator(func):
    """Wraps a function to print start/end messages."""
    # We use *args and **kwargs to accept ANY arguments
    def wrapper(*args, **kwargs):
        print(f" Starting {func.__name__}...")
        
        # Forward the arguments to the original function
        result = func(*args, **kwargs)
        
        print(f" Finished {func.__name__}!")
        return result
    return wrapper

@logger_decorator
def say_hello(name):
    print(f"Hello {name}!")

# Calling the decorated function
say_hello("Alice")

# Output:
#  Starting say_hello...
# Hello Alice!
#  Finished say_hello!`} />
                <ProTip><p>
                        <strong>Why `*args`?</strong> A truly reusable decorator shouldn't care about the signature of the function it wraps.
                        By using argument unpacking (`*args`, `**kwargs`), we ensure our wrapper can handle any function, whether it takes 0 arguments or 100.
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>Functional Programming Tools</h2>
                <p>
                    Python is not a purely functional language (like Haskell), but it borrows heavily from the paradigm.
                    The `map`, `filter`, and `reduce` functions allow you to process iterables without explicit `for` loops.
                </p>

                <h3>1. Map (Apply logic to all)</h3>
                <CodeBlock language="python" code={`temps_c = [0, 20, 100]

# Standard Loop
temps_f = []
for t in temps_c:
    temps_f.append((t * 9/5) + 32)

# Functional Map
# Syntax: map(function, iterable)
temps_f = list(map(lambda t: (t * 9/5) + 32, temps_c))`} />

                <h3>2. Filter (Select specific items)</h3>
                <CodeBlock language="python" code={`users = [{"name": "Alice", "active": True}, {"name": "Bob", "active": False}]

# Syntax: filter(function, iterable)
# Keep only if function returns True
active_users = list(filter(lambda u: u["active"], users))`} />

                <ProTip><p><strong>Note:</strong> In modern Python, <strong>List Comprehensions</strong> are often preferred over `map` and `filter` because they are more readable and slightly faster.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>Theory: Pure Functions vs Side Effects</h2>
                <p>
                    To write "bug-free" code, you should strive for <strong>Pure Functions</strong>. A pure function has two properties:
                </p>
                <ol>
                    <li><strong>Deterministic:</strong> Given the same input, it <em>always</em> returns the same output.</li>
                    <li><strong>No Side Effects:</strong> It does not modify global variables, write to files, or change input arguments.</li>
                </ol>

                <CodeBlock language="python" code={`#  Impure Function
global_tax = 0.05

def calculate_total(price):
    # Depends on global state (Bad!)
    return price * (1 + global_tax)

# Pure Function
def calculate_total(price, tax_rate):
    # Depends ONLY on arguments
    return price * (1 + tax_rate)

# Why? Testing the pure function is trivial. 
# Testing the impure one requires mocking the global state.`} />
            </div>

            <div className="content-section">
                <h2>Capstone Project: Data Transformation Pipeline</h2>
                <p>
                    Let's use our knowledge of functions to build a robust data processing pipeline.
                    Instead of one giant script, we will create small, specialized functions and compose them.
                </p>
                <p>
                    <strong>The Goal:</strong> Process a list of raw user logs, clean them, normalize emails, and filter out bots.
                </p>
                <ProTip><p>
                        <strong>The Unix Philosophy:</strong> "Make each program do one thing well."
                        We will apply this to our functions. Instead of one giant 50-line function, we will build small, reusable "Lego blocks" and snap them together.
                    </p></ProTip>

                <CodeBlock language="python" code={`raw_logs = [
    {"id": 1, "email": "ALICE@EXAMPLE.COM ", "role": "admin"},
    {"id": 2, "email": " bob@example.com", "role": "user"},
    {"id": 3, "email": "bot_99@crawler.net", "role": "bot"},
]

# 1. Specialized Transformation Functions
def clean_email(user):
    """Trims whitespace and lowercases email."""
    new_user = user.copy() # Important: Don't mutate input!
    new_user["email"] = user["email"].strip().lower()
    return new_user

def is_human(user):
    """Returns True if user is not a bot."""
    return user["role"] != "bot"

def add_display_name(user):
    """Extracts name from email address."""
    new_user = user.copy()
    new_user["display"] = new_user["email"].split("@")[0].capitalize()
    return new_user

# 2. Composition (The Pipeline)
def run_pipeline(data):
    # Step 1: Clean Emails
    cleaned = map(clean_email, data)
    
    # Step 2: Remove Bots
    humans = filter(is_human, cleaned)
    
    # Step 3: Add Metadata
    final_users = map(add_display_name, humans)
    
    return list(final_users)

# 3. Execution
processed_data = run_pipeline(raw_logs)

import json
print(json.dumps(processed_data, indent=2))

# Output:
# [
#   {
#     "id": 1, 
#     "email": "alice@example.com", 
#     "role": "admin", 
#     "display": "Alice"
#   },
#   {
#     "id": 2, 
#     "email": "bob@example.com", 
#     "role": "user", 
#     "display": "Bob"
#   }
# ]`} />
                <ProTip><p><strong>Victory!</strong> We processed complex data without a single nested loop or `if` statement. This is the power of functional programming.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>The "Lazy" Function: Generators & Yield</h2>
                <p>
                    Standard functions (`return`) calculate everything at once and return a single value (or list).
                    <strong>Generators</strong> (`yield`) are functions that can "pause" their execution and resume later.
                    This concept is known as <strong>Lazy Evaluation</strong>.
                </p>
                <p>
                    When you call a generator function, it doesn't run immediately. It returns a <strong>Generator Object</strong>.
                    The code only executes when you call the built-in `next()` function on that object.
                </p>
                <ProTip><p>
                        <strong>StopIteration:</strong> When the function finishes (hits the end or a `return`), it raises a `StopIteration` exception.
                        A `for` loop automatically catches this exception to stop iterating. This is the magic protocol that makes loops work!
                    </p></ProTip>
                <p>
                    They are building blocks for memory-efficient pipelines. A generator function returns an <em>iterator</em>, not data.
                </p>

                <h3>Memory Showdown: Return vs Yield</h3>
                <CodeBlock language="python" code={`import sys

# 1. Eager Loading (Standard)
def eager_range(n):
    return [i for i in range(n)]

# 2. Lazy Loading (Generator)
def lazy_range(n):
    for i in range(n):
        yield i

N = 1_000_000

list_obj = eager_range(N)
gen_obj = lazy_range(N)

print(f"List Size: {sys.getsizeof(list_obj)} bytes") # ~8 MB
print(f"Gen Size:  {sys.getsizeof(gen_obj)} bytes")  # ~100 bytes!

# Why? The list stores 1 million ints. 
# The generator only stores "current state" and "logic to get next".`} />
            </div>

            <div className="content-section">
                <h2>The Modern Era: Async and Await</h2>
                <p>
                    Since Python 3.5, functions have a new superpower: they can be <strong>Asynchronous</strong>.
                    This breaks the standard rule that "Lines execute one after another, blocking until finished."
                </p>
                <ProTip><p><strong>Use Case:</strong> Async is critical for I/O operations (Web requests, Database queries) where the CPU spends most of its time <em>waiting</em> for a response.</p></ProTip>

                <h3>The Coffee Shop Analogy</h3>
                <p>
                    <strong>Synchronous (Blocking):</strong> You order a coffee. The barista stares at the machine while it brews. You wait. The next customer waits outside. NOTHING else happens until your coffee is done.
                </p>
                <p>
                    <strong>Asynchronous (Non-Blocking):</strong> You order a coffee. The barista gives you a ticket and takes the next order immediately. While the machine brews (I/O), the barista processes payments for 10 other people (CPU).
                </p>

                <h3>1. Defining Coroutines (`async def`)</h3>
                <p>
                    By adding `async` before `def`, you define a <strong>Coroutine</strong>. Calling it doesn't run code; it returns a coroutine object (just like a generator!).
                </p>

                <h3>2. Pausing Execution (`await`)</h3>
                <p>
                    Inside a coroutine, you can use `await` to pause your function and yield control back to the <strong>Event Loop</strong>.
                </p>

                <CodeBlock language="python" code={`import asyncio
import time

async def fetch_data(id):
    print(f"Start fetching {id}...")
    # Simulate a network delay (non-blocking!)
    await asyncio.sleep(1) 
    print(f"Finished {id}")
    return {"id": id, "data": "Secret"}

async def main():
    start = time.time()
    
    # Run these drastically faster (concurrently!)
    # We schedule 3 calls at the same time:
    results = await asyncio.gather(
        fetch_data(1),
        fetch_data(2),
        fetch_data(3)
    )
    
    # Total time will be ~1 second, not 3 seconds!
    print(f"Total Time: {time.time() - start:.2f}s")
    print(results)

# Starting the Event Loop
asyncio.run(main())`} />
                <p>
                    This is how libraries like <strong>FastAPI</strong> achieve insane performance. They handle thousands of requests per second because they don't block the CPU while waiting for the Database.
                </p>
            </div>

            <div className="content-section">
                <h2>Advanced Tool: Partial Functions</h2>
                <p>
                    Sometimes you have a function with many arguments, but you want to "freeze" some of them to create a simpler version.
                    You can do this with closures (as seen above), or use the standard library `functools.partial`.
                </p>

                <CodeBlock language="python" code={`from functools import partial

def power(base, exponent):
    return base ** exponent

# Create a specialized function where 'exponent' is always 2
square = partial(power, exponent=2)

# Create a specialized function where 'exponent' is always 3
cube = partial(power, exponent=3)

print(square(10)) # 100
print(cube(10))   # 1000

# Real World Use:
# Button(text="Click Me", command=partial(open_window, "settings_page"))`} />
            </div>

            <div className="content-section">
                <h2>Introspection: Looking Inside Functions</h2>
                <p>
                    Since functions are objects, Python allows you to inspect them at runtime.
                    This is called <strong>Introspection</strong>. You can see variable names, defaults, and documentation dynamically.
                </p>

                <CodeBlock language="python" code={`def secret_logic(a, b=10):
    """Hidden docstring."""
    c = a + b
    return c

print(secret_logic.__name__) # "secret_logic"
print(secret_logic.__doc__)  # "Hidden docstring."
print(secret_logic.__defaults__) # (10,)`} />

                <h3>The Secret Life of Function Attributes</h3>
                <p>
                    Did you know functions can have their own variables?
                    Since a function is an object, you can attach data to it.
                    This is often used for simple caching or counting without using a global variable or Class.
                </p>
                <CodeBlock language="python" code={`def tracker():
    tracker.count += 1
    print(f"I have been run {tracker.count} times")

# Initialize the attribute
tracker.count = 0

tracker() # I have been run 1 times
tracker() # I have been run 2 times`} />
                <ProTip><p><strong>Pro Tip:</strong> While cool, this is often considered "magic". Use a Class or Closure if you need robust state.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>Pro Tip: Function Overloading</h2>
                <p>
                    Languages like Java allow you to define multiple functions with the same name but different arguments (`add(int, int)` vs `add(str, str)`).
                    Python is dynamic, so it doesn't support this natively.
                </p>
                <p>
                    However, we can achieve <strong>Single Dispatch</strong> (generic functions) using the standard library.
                </p>

                <CodeBlock language="python" code={`from functools import singledispatch

@singledispatch
def process_data(data):
    raise NotImplementedError("Unknown type!")

@process_data.register(str)
def _(data):
    print(f"Processing string: {data.upper()}")

@process_data.register(list)
def _(data):
    print(f"Processing list: {sum(data)}")

@process_data.register(int)
def _(data):
    print(f"Processing number: {data * 2}")

process_data("hello") # Processing string: HELLO
process_data([1, 2, 3]) # Processing list: 6
process_data(10)      # Processing number: 20

# This is much cleaner than a giant 'if isinstance(data, str)... elif...' chain!`} />
            </div>

            <div className="content-section">
                <h2>Quality Assurance: Testing Your Functions</h2>
                <p>
                    Because functions are isolated units of logic, they are easy to test.
                    Python includes a built-in module called `doctest` that allows you to write tests <em>inside your documentation</em>.
                </p>

                <CodeBlock language="python" code={`def square(n):
    """
    Returns the square of a number.

    >>> square(2)
    4
    >>> square(-3)
    9
    >>> square(0)
    0
    """
    return n * n

if __name__ == "__main__":
    import doctest
    doctest.testmod()
    print("Tests finished! (No output means success)")`} />
                <ProTip><p><strong>Why use this?</strong> It serves two purposes: it documents how to use the function AND verifies it works. If you change the logic and break the test, Python will scream at you.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>Refactoring Case Study: From Spaghetti to Elegant</h2>
                <p>
                    Let's look at a real-world example of how functions improve code quality.
                    We will refactor a monolithic script into a modular design.
                </p>

                <h3>Before: The "Script" Approach</h3>
                <CodeBlock language="python" code={`# The Spaghetti Monolith
import random
import { ProTip } from '../../components/LessonUI';
import { ProTip } from '../../components/LessonUI';

print("Welcome to the Number Game!")
lower = 1
upper = 100
target = random.randint(lower, upper)
attempts = 0

while True:
    guess_str = input(f"Guess a number between {lower} and {upper}: ")
    if not guess_str.isdigit():
        print("Invalid input!")
        continue
    
    guess = int(guess_str)
    attempts += 1
    
    if guess < target:
        print("Too low!")
    elif guess &gt; target:
        print("Too high!")
    else:
        print(f"Correct! It took {attempts} attempts.")
        break`} />
                <p><strong>Problems:</strong> Logic is mixed with UI. Constants are hardcoded. It's not reusable.</p>

                <h3>After: The Functional Approach</h3>
                <CodeBlock language="python" code={`import random

def get_valid_input(prompt):
    """Handles UI interaction and validation."""
    while True:
        data = input(prompt)
        if data.isdigit():
            return int(data)
        print(" Please enter a valid integer.")

def play_round(target):
    """Handles the logic of a single guess."""
    guess = get_valid_input("Your guess: ")
    
    if guess < target:
        print(" Too low!")
        return False
    elif guess &gt; target:
        print(" Too high!")
        return False
    else:
        print(" Correct!")
        return True

def run_game(lower=1, upper=100):
    """Orchestrates the game flow."""
    print(f" Starting Game ({lower}-{upper})")
    target = random.randint(lower, upper)
    attempts = 0
    
    while True:
        attempts += 1
        is_won = play_round(target)
        if is_won:
            print(f" Victory in {attempts} moves!")
            break

# Now we can just call it!
if __name__ == "__main__":
    run_game()`} />
                <ProTip><p><strong>Benefits:</strong> We customized `lower/upper` bounds easily. The logical "check" is separated from the "input" loop. This code is testable and expandable.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>Summary: Best Practices Checklist</h2>
                <ProTip><ul>
                        <li><strong>Small & Focused:</strong> A function should do <em>one</em> thing well.</li>
                        <li><strong>Docstrings:</strong> always document arguments and return values.</li>
                        <li><strong>Pure Functions:</strong> Avoid side effects whenever possible.</li>
                        <li><strong>Returns:</strong> Always return data; avoid printing inside logic functions.</li>
                        <li><strong>Naming:</strong> Use <code>snake_case</code> and verbs (e.g., <code>calculate_total</code>, not <code>total_calc</code>).</li>
                    </ul></ProTip>
            </div>

        </div>
    );
};

export default PythonFunctions;

